import classNames from 'classnames';
import { type CSSProperties, useEffect, useRef } from 'react';
import type { ReactElement, RefObject } from 'react';
import Link from 'next/link';
import type { LocationItemProps } from './LocationDropdown.data';
import styles from './LocationDropdown.module.scss';
import {
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
  setupMouseEnterTimeline,
  setupMouseLeaveTimeline,
} from './LocationItem.transitions';
import { useTransitionController } from 'util/react-transition-component';
import Heading from '../../atoms/heading/Heading';

import type { HeadingRef } from '../../atoms/heading/Heading.data';

export default function LocationItem({
  ref,
  className,
  isOpen,
  location,
  index,
  iconColor,
}: LocationItemProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const backgroundRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLSpanElement>(null);
  const headingRef = useRef<HeadingRef>(null);

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
        headingRef,
        lineRef,
        index,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  const hoverTransitionController = useTransitionController(
    () => ({
      refs: {
        backgroundRef,
        headingRef,
      },
      setupTransitionInTimeline: setupMouseEnterTimeline,
      setupTransitionOutTimeline: setupMouseLeaveTimeline,
    }),
    [],
  );

  useEffect(() => {
    if (isOpen) {
      transitionController.transitionIn();
    } else {
      transitionController.transitionOut();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const style = iconColor ? ({ '--accent-color': iconColor } as CSSProperties) : undefined;

  return (
    <li
      className={className}
      onMouseEnter={() => hoverTransitionController.transitionIn()}
      onMouseLeave={() => hoverTransitionController.transitionOut()}
      style={style}
    >
      <Link href={location.office_link.cached_url} className={styles.locationItemLink}>
        <div ref={backgroundRef} className={classNames(styles.background, 'abs-fill')} />
        <span ref={lineRef} className={styles.listLine} />
        <Heading ref={headingRef} as="h4" size="heading-04" transitionInType="slideUpFadeIn">
          {location.city}
        </Heading>
      </Link>
    </li>
  );
}
